import React from 'react';
import { pct, numFormat, shortDate, Section } from '../analyticsUi';
import { BACKTEST_AGE } from './profitModel';
import { eur } from './profitFormat';

export function SetupChecklist({ health }) {
  const item = (done, text) => (
    <li className={done ? 'done' : ''}><span className="pf-check">{done ? '✓' : '○'}</span>{text}</li>
  );
  return (
    <ol className="pf-setup">
      {item(health?.hasAnalyticsExport, <>Firebase console → Project settings → Integrations → <b>BigQuery</b> → enable <b>Google Analytics</b> export (daily).</>)}
      {item(health?.hasAdRevenue, <>AdMob → Settings → <b>Linked services</b> → link the Firebase project, so ad revenue is logged per user.</>)}
      {item(health?.hasAdsTransfer, <>BigQuery console → <b>Data transfers</b> → Create → <b>Google Ads</b>, daily, into a dataset such as <code>google_ads</code>. Backfill a few months of history.</>)}
      {item(health?.revenueEvents > 0, <>RevenueCat → Integrations → <b>Webhooks</b> → URL of the <code>revenuecatWebhook</code> function, with the Authorization header set to the <code>REVENUECAT_WEBHOOK_SECRET</code> secret.</>)}
      {item(health?.exportTransactions > 0, <>RevenueCat → <b>Scheduled data exports</b> (GCP) → bucket <code>norppa-cw-revenuecat-exports</code>, folder <code>transactions/</code>, CSV, new and updated transactions only. The first export brings in the full purchase history.</>)}
      <li><span className="pf-check">·</span>The Analytics export does not backfill, so installs and ad revenue per user start from the day it was enabled.</li>
    </ol>
  );
}

function HealthItem({ ok, label }) {
  const state = ok === null || ok === undefined ? 'info' : ok ? 'good' : 'bad';
  const icon = { good: '✓', bad: '!', info: 'i' }[state];
  return (
    <div className={`pf-health-item ${state}`}>
      <span className="pf-health-icon" aria-hidden="true">{icon}</span>
      {label}
    </div>
  );
}

export function Accuracy({ accuracy }) {
  if (!accuracy) return null;
  return (
    <Section
      title="How accurate are the forecasts?"
      hint={`Past forecasts of first ${BACKTEST_AGE} days earnings against what users actually earned.`}
    >
      <div className="pf-table-wrap">
        <table className="pf-table">
          <thead><tr><th>Forecast made</th><th>New users</th><th>Guessed per user</th><th>Actual per user</th><th>Off by</th></tr></thead>
          <tbody>
            {accuracy.rows.map(r => (
              <tr key={r.cutoff}>
                <td>{shortDate(r.cutoff)} {r.cutoff.slice(0, 4)}</td>
                <td>{numFormat.format(r.users)}</td>
                <td>{eur(r.predicted, true)}</td>
                <td>{eur(r.actual, true)}</td>
                <td>{r.error > 0 ? '+' : ''}{Math.round(r.error * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="pf-footnote">Plus means too optimistic.</p>
    </Section>
  );
}

export function DataHealth({ health, paidShare }) {
  const healthItems = [
    { ok: health.hasAnalyticsExport, label: 'Analytics export' },
    { ok: health.hasAdsTransfer, label: `Google Ads spend${health.adsCurrency ? ` (${health.adsCurrency})` : ''}` },
    { ok: health.hasAdsTransfer ? !!health.hasBudgetHistory : null, label: health.hasBudgetHistory ? 'Google Ads budget history' : 'Google Ads budget history: not in the transfer yet (budget periods fall back to markers)' },
    {
      ok: health.adRevenueFromGa4 ? null : true,
      label: !health.adRevenueFromGa4
        ? 'Ad revenue per user from BigQuery (ad_paid events)'
        : health.hasAdRevenue
          ? 'ad_paid events arriving, but not yet from enough users; using GA4 until the new app version is widespread'
          : 'No ad revenue in BigQuery yet (app version with ad_paid not released); using GA4',
    },
    {
      ok: health.ga4AdCohortWeeks > 0,
      label: health.ga4AdCohortWeeks > 0
        ? `Ad revenue per user: estimated from ${health.ga4AdCohortWeeks} install weeks in GA4`
        : 'Ad revenue per user: not estimated',
    },
    {
      ok: !!health.ga4HistoryFrom,
      label: health.ga4HistoryFrom ? `GA4 history (daily totals): ${health.ga4HistoryFrom} → ${health.ga4HistoryUntil}` : 'GA4 history: not loaded',
    },
    { ok: health.revenueEvents > 0, label: `RevenueCat webhook events: ${numFormat.format(health.revenueEvents || 0)}` },
    {
      ok: health.exportTransactions > 0,
      label: health.exportTransactions > 0
        ? `RevenueCat history: ${numFormat.format(health.exportTransactions)} transactions (to ${new Date(health.exportCutoff).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`
        : 'RevenueCat history export: not received yet',
    },
    { ok: health.signupsMatchedPct == null ? null : health.signupsMatchedPct >= 0.5, label: `New users found in Analytics: ${pct(health.signupsMatchedPct)}` },
    { ok: health.subRevenueMatchedPct == null ? null : health.subRevenueMatchedPct >= 0.8, label: `Purchases matched to a user: ${pct(health.subRevenueMatchedPct)}` },
    { ok: null, label: `Installs credited to ads (30 days): ${pct(paidShare)}` },
  ];
  const healthChecked = healthItems.filter(h => h.ok != null);
  const healthOk = healthChecked.filter(h => h.ok).length;

  return (
    <section className="pf-section pf-section-quiet">
      <details open={healthOk < healthChecked.length}>
        <summary className="pf-health-summary">
          <h2>Data health</h2>
          <span className={`pf-health-count ${healthOk < healthChecked.length ? 'bad' : 'good'}`}>
            {healthOk} of {healthChecked.length} connected
          </span>
        </summary>
        <p className="pf-hint">Low percentages make the numbers less reliable.</p>
        <div className="pf-health">
          {healthItems.map(h => <HealthItem key={h.label} ok={h.ok} label={h.label} />)}
        </div>
        <details className="pf-details">
          <summary>Setup checklist</summary>
          <SetupChecklist health={health} />
        </details>
      </details>
    </section>
  );
}
